<template>
  <nav class="desktop-role-nav" :aria-label="$t('nav.navigation')">
    <div v-for="item in items" :key="item.label" class="desktop-role-nav__entry">
      <button
        v-if="item.children"
        type="button"
        class="desktop-role-nav__link"
        :class="{ active: groupActive(item) }"
        @click="toggleGroup(item.label)"
      >
        <CIcon :name="item.icon" size="lg" />
        <span>{{ $t(item.label) }}</span>
        <CIcon name="cil-chevron-bottom" size="sm" />
      </button>
      <router-link
        v-else
        :to="item.path"
        class="desktop-role-nav__link"
        :class="{ active: isActive(item) }"
      >
        <CIcon :name="item.icon" size="lg" />
        <span>{{ $t(item.label) }}</span>
      </router-link>

      <div v-if="item.children && openGroup === item.label" class="desktop-role-nav__dropdown">
        <button
          v-for="child in item.children"
          :key="itemKey(child)"
          type="button"
          class="desktop-role-nav__dropdown-item"
          :class="{ active: isActive(child) }"
          @click="navigate(child)"
        >
          <CIcon :name="child.icon" size="lg" />
          <span>{{ $t(child.label) }}</span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
const roleItems = {
  student_affairs: [
    { label: 'nav.dashboard', path: '/student-affairs/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/student-affairs/home', icon: 'cil-home' },
    { label: 'nav.userManual', path: '/student-affairs/manual', icon: 'cil-book' },
  ],
  user: [
    { label: 'nav.main', path: '/user/mainuser', icon: 'cil-home' },
    { label: 'nav.history', path: '/user/history', icon: 'cil-history' },
    { label: 'nav.userManual', path: '/user/manual', icon: 'cil-book' },
  ],
  admin: [
    { label: 'nav.dashboard', path: '/admin/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/admin/mainadmin', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/admin/history', icon: 'cil-history' },
    { label: 'nav.manageData', icon: 'cil-settings', children: [
      { label: 'nav.staff', path: '/admin/UsersList', icon: 'cil-people' },
      { label: 'nav.jobTypes', path: '/admin/job-type', icon: 'cil-briefcase' },
      { label: 'nav.buildings', path: '/admin/building', icon: 'cil-building' },
      { label: 'nav.requesterOrgSync', path: '/admin/requester-organization-sync', icon: 'cil-sync' },
      { label: 'nav.evaluation', path: '/admin/evaluation', icon: 'cil-star' },
    ] },
    { label: 'nav.userManual', path: '/admin/manual', icon: 'cil-book' },
  ],
  supervisor: [
    { label: 'nav.dashboard', path: '/supervisor/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/supervisor/mainsupervisor', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/supervisor/history', icon: 'cil-history' },
    { label: 'nav.manageData', icon: 'cil-settings', children: [
      { label: 'nav.staff', path: '/supervisor/UsersList', icon: 'cil-people' },
      { label: 'nav.jobTypes', path: '/supervisor/job-type', icon: 'cil-briefcase' },
      { label: 'nav.evaluation', path: '/supervisor/evaluation', icon: 'cil-star' },
    ] },
    { label: 'nav.userManual', path: '/supervisor/manual', icon: 'cil-book' },
  ],
  technician: [
    { label: 'nav.dashboard', path: '/technician/dashboard', icon: 'cil-speedometer' },
    { label: 'nav.main', path: '/technician/maintechnician', icon: 'cil-home' },
    { label: 'reportList.tabHistory', path: '/technician/history', icon: 'cil-history' },
    { label: 'nav.userManual', path: '/technician/manual', icon: 'cil-book' },
  ],
}

export default {
  name: 'RoleDesktopTopNav',
  props: { role: { type: String, required: true } },
  data() { return { openGroup: null } },
  computed: { items() { return roleItems[this.role] || [] } },
  watch: { '$route.fullPath'() { this.openGroup = null } },
  methods: {
    itemKey(item) { return `${item.path}-${item.tab || ''}` },
    toggleGroup(label) { this.openGroup = this.openGroup === label ? null : label },
    groupActive(item) { return item.children.some(child => this.isActive(child)) },
    isActive(item) {
      if (item.tab) return this.$route.path === item.path && (this.$route.query.tab || 'report') === item.tab
      return this.$route.path === item.path || this.$route.path.startsWith(`${item.path}/`)
    },
    navigate(item) {
      this.openGroup = null
      const target = item.tab ? { path: item.path, query: { tab: item.tab } } : { path: item.path }
      const fullPath = item.tab ? `${item.path}?tab=${item.tab}` : item.path
      if (this.$route.fullPath !== fullPath) this.$router.push(target)
    },
  },
}
</script>

<style scoped>
.desktop-role-nav { display: none; align-items: center; align-self: stretch; gap: 2px; margin: 0 8px; flex: 0 1 auto; }
.desktop-role-nav__entry { position: relative; display: flex; height: 100%; align-items: center; }
.desktop-role-nav__link { display: flex; min-height: 40px; align-items: center; gap: 6px; padding: 7px 10px; color: #64748b; background: transparent; border: 0; border-radius: 8px; font: inherit; font-size: 14px; font-weight: 700; text-decoration: none; white-space: nowrap; }
.desktop-role-nav__link:hover { color: #1e40af; background: rgba(37, 99, 235, 0.06); text-decoration: none; }
.desktop-role-nav__link.active { color: #fff; background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%); box-shadow: 0 2px 8px rgba(30, 58, 138, 0.25); }
.desktop-role-nav__dropdown { position: absolute; top: calc(100% - 8px); left: 0; z-index: 1200; width: max-content; min-width: 210px; padding: 6px; background: #fff; border: 1px solid rgba(37, 99, 235, 0.14); border-radius: 10px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.18); }
.desktop-role-nav__dropdown-item { display: flex; width: 100%; align-items: center; gap: 8px; padding: 8px 10px; color: #475569; background: transparent; border: 0; border-radius: 7px; font: inherit; font-size: 13.5px; font-weight: 600; text-align: left; }
.desktop-role-nav__dropdown-item:hover, .desktop-role-nav__dropdown-item.active { color: #1e40af; background: #f0f9ff; }
@media (min-width: 1201px) { .desktop-role-nav { display: flex; } }
@media (min-width: 1400px) {
  .desktop-role-nav { gap: 4px; margin: 0 14px; }
  .desktop-role-nav__link { padding: 8px 12px; font-size: 15px; }
}
</style>
